// X-Ray Cohort — executive-dashboard design system with Sagicor accents.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { isExecutiveRole } from "./_execProtected";
import { isSbblHeld } from "./_sbblHold";
import MethodologyPanel from "./MethodologyPanel";

const API = process.env.REACT_APP_BACKEND_URL;

// Sagicor brand
const SAGICOR_BLUE = "#00B5EF";
const SAGICOR_BLUE_DARK = "#0099CC";
const SAGICOR_GREEN = "#82C341";
const SAGICOR_GREEN_DARK = "#5F9A2A";
const SAGICOR_BLUE_PALE = "#E6F8FD";
const SAGICOR_GREEN_PALE = "#F1F8E4";

// v4.0 forest palette for tier badges (methodology contract)
const TIER_COLOR = {
  "Core Builder":             { bg: "#00583D", fg: "#FFFFFF" },
  "Emerging Builder":         { bg: "#3B7A5C", fg: "#FFFFFF" },
  "Support Developer":        { bg: "#C28F2C", fg: "#1F2933" },
  "Support Developer (PRR)":  { bg: "#B07E2C", fg: "#FFFFFF" },
  "Restructuring Candidate":  { bg: "#A85D5D", fg: "#FFFFFF" },
  "No Data":                  { bg: "#D8DDE3", fg: "#1F2933" },
};

// Executive-dashboard style tokens (CSS variable backed)
const crd = {
  background: "var(--surface-card)",
  border: "var(--border-width) solid var(--border-default)",
  borderRadius: "1rem",
  boxShadow: "var(--shadow-card)",
  transition: "background 150ms ease, border-color 150ms ease",
};
const tp = { color: "var(--text-primary)" };
const ts = { color: "var(--text-secondary)" };
const tm = { color: "var(--text-muted)" };
const mono = { fontFamily: "var(--font-mono)" };
const labelS = { ...tm, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 };

function TierBadge({ tier }) {
  const s = TIER_COLOR[tier] || { bg: "#D8DDE3", fg: "#1F2933" };
  return (
    <span
      data-testid={`tier-badge-${tier}`}
      style={{
        backgroundColor: s.bg, color: s.fg, padding: "3px 10px", borderRadius: 999,
        fontWeight: 600, fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase",
        whiteSpace: "nowrap", display: "inline-block",
      }}
    >
      {tier || "—"}
    </span>
  );
}

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toISOString().replace("T", " ").slice(0, 16) + " UTC"; }
  catch { return iso; }
};

// Canonical tier order for chip display (drives left-to-right ordering)
const TIER_ORDER = [
  "Core Builder",
  "Emerging Builder",
  "Support Developer",
  "Support Developer (PRR)",
  "Restructuring Candidate",
  "No Data",
];

export default function AdminXrayCohort() {
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "loading", items: [], error: null });
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState(null); // null = all
  const [regionFilter, setRegionFilter] = useState("ALL");
  const [runner, setRunner] = useState(null);

  const isExecRoute = window.location.pathname.startsWith("/executive");
  const loginPath = isExecRoute ? "/executive/login" : "/admin/login";
  // Demo executives (e.g., Neil Freyke) live under /executive/demo/dashboard.
  // Production executives use /executive/dashboard. Admins go back to /admin.
  const _isDemoExec = (() => {
    try { return !!JSON.parse(localStorage.getItem("userInfo") || "{}")?.isDemoAccount; } catch { return false; }
  })();
  const backPath = isExecRoute
    ? (_isDemoExec ? "/executive/demo/dashboard" : "/executive/dashboard")
    : "/admin";

  useEffect(() => {
    // When opened under /executive/... the token is stored as `executiveToken`;
    // when opened under /admin/... it is `adminToken` (with `authToken` as legacy fallback).
    const token = isExecRoute
      ? (localStorage.getItem("executiveToken") || localStorage.getItem("adminToken") || localStorage.getItem("authToken"))
      : (localStorage.getItem("adminToken") || localStorage.getItem("authToken"));
    if (!token) { navigate(loginPath, { replace: true }); return; }
    const refresh = () => {
      axios.get(`${API}/api/admin/xray-list`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => {
          // Per Sagicor leadership directive: AVP+/VP+/Chief/CIO/CISO/CTO must not appear
          // in the cohort listing. Sagicor Bank Barbados (SBBL) candidates are also on hold
          // until SBBL reporting is approved. Records remain in the DB; this is a UI-level exclusion.
          const all = res.data.items || [];
          const items = all.filter((r) => !isExecutiveRole(r.role) && !isSbblHeld(r));
          setState({ status: "ready", items, error: null });
        })
        .catch((e) => {
          const status = e?.response?.status;
          if (status === 401) {
            if (isExecRoute) localStorage.removeItem("executiveToken");
            else localStorage.removeItem("adminToken");
            navigate(loginPath, { replace: true });
            return;
          }
          if (status === 403) { setState({ status: "denied", items: [], error: "Not on X-Ray preview allowlist." }); return; }
          setState({ status: "error", items: [], error: e?.response?.data?.detail || e.message });
        });
      axios.get(`${API}/api/admin/xray-cohort-status`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => setRunner(res.data))
        .catch(() => setRunner(null));
    };
    refresh();
    const id = setInterval(refresh, 30000); // refresh every 30s
    return () => clearInterval(id);
  }, [navigate]);

  // Tier counts across the FULL cohort (ignore search/region filters so chips remain stable)
  const tierCounts = useMemo(() => {
    const counts = {};
    for (const r of state.items) {
      const k = r.tier || "No Data";
      counts[k] = (counts[k] || 0) + 1;
    }
    return counts;
  }, [state.items]);

  // All unique regions seen in the cohort (for the region dropdown)
  const regionOptions = useMemo(() => {
    const set = new Set();
    for (const r of state.items) {
      if (r.region && String(r.region).trim()) set.add(String(r.region).trim());
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [state.items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return state.items.filter((r) => {
      if (tierFilter && (r.tier || "No Data") !== tierFilter) return false;
      if (regionFilter !== "ALL" && (r.region || "") !== regionFilter) return false;
      if (q) {
        const hit = [r.name, r.role, r.region, r.tier, r.verdict, r.candidate_id]
          .filter(Boolean).some((s) => String(s).toLowerCase().includes(q));
        if (!hit) return false;
      }
      return true;
    });
  }, [search, tierFilter, regionFilter, state.items]);

  if (state.status === "loading") {
    return <div data-testid="xray-cohort-loading" className="p-10 text-sm" style={ts}>Loading X-Ray cohort…</div>;
  }
  if (state.status === "denied") {
    return <div data-testid="xray-cohort-denied" className="p-10 text-sm" style={{ color: "#A85D5D" }}>Access denied. {state.error}</div>;
  }
  if (state.status === "error") {
    return <div data-testid="xray-cohort-error" className="p-10 text-sm" style={{ color: "#A85D5D" }}>Failed to load X-Ray cohort: {String(state.error)}</div>;
  }

  return (
    <div
      data-testid="xray-cohort-page"
      style={{ background: "var(--surface-page)", minHeight: "100vh", padding: "32px 32px 64px" }}
    >
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        {/* Header */}
        <div className="flex justify-between items-start mb-8" data-testid="cohort-header">
          <div className="flex items-center gap-4">
            <img
              src="/branding/sagicor-logo-stacked.png"
              alt="Sagicor"
              data-testid="sagicor-logo-header"
              style={{ height: 56, width: "auto" }}
            />
            <div>
              <h1 className="text-3xl font-semibold tracking-tight" style={tp}>X-Ray Cohort</h1>
              <p className="text-sm mt-1" style={ts}>
                Sagicor Technology Capability Assessment · Methodology v4.0 · Schema v4.3
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-2 justify-end">
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: SAGICOR_GREEN }} />
              <span className="text-xs font-medium" style={{ color: SAGICOR_GREEN_DARK }}>LIVE</span>
            </div>
            <p className="text-2xl font-semibold mt-1" style={{ ...tp, ...mono }}>
              {state.items.length}
              <span className="text-sm font-normal ml-2" style={ts}>X-Rays generated</span>
            </p>
          </div>
        </div>

        {/* Cohort runner live banner removed per stakeholder request (2026-05-25):
            the cohort generation is complete, so the operator-facing progress
            banner is no longer relevant on the Executive / Admin view. */}

        {/* KPI strip */}
        <div className="grid grid-cols-3 gap-4 mb-6" data-testid="cohort-kpi-strip">
          <div style={crd} className="p-5 text-center">
            <p className="text-2xl font-semibold tracking-tight" style={{ ...mono, ...tp }}>{state.items.length}</p>
            <p className="mt-2" style={labelS}>Generated</p>
          </div>
          <div style={crd} className="p-5 text-center">
            <p className="text-2xl font-semibold tracking-tight" style={{ ...mono, ...tp }}>v4.0</p>
            <p className="mt-2" style={labelS}>Methodology</p>
          </div>
          <div style={crd} className="p-5 text-center">
            <p className="text-2xl font-semibold tracking-tight" style={{ ...mono, color: SAGICOR_GREEN_DARK }}>v4.3</p>
            <p className="mt-2" style={labelS}>Schema</p>
          </div>
        </div>

        {/* Tier filter chips (with counts) */}
        <div data-testid="tier-filter-chips" className="mb-3">
          <div style={labelS} className="mb-2">Filter by Level</div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="tier-chip-all"
              onClick={() => setTierFilter(null)}
              style={{
                padding: "6px 14px",
                borderRadius: 999,
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                background: tierFilter === null ? `linear-gradient(135deg, ${SAGICOR_BLUE} 0%, ${SAGICOR_GREEN} 100%)` : "var(--surface-inner)",
                color: tierFilter === null ? "#FFF" : "var(--text-secondary)",
                border: tierFilter === null ? "0" : "var(--border-width) solid var(--border-default)",
                transition: "all 150ms ease",
              }}
            >
              All <span style={{ ...mono, marginLeft: 6, opacity: 0.9 }}>{state.items.length}</span>
            </button>
            {TIER_ORDER.filter((t) => tierCounts[t]).map((tier) => {
              const c = TIER_COLOR[tier] || { bg: "#D8DDE3", fg: "#1F2933" };
              const active = tierFilter === tier;
              return (
                <button
                  key={tier}
                  data-testid={`tier-chip-${tier}`}
                  onClick={() => setTierFilter(active ? null : tier)}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 999,
                    cursor: "pointer",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    background: active ? c.bg : "var(--surface-inner)",
                    color: active ? c.fg : "var(--text-primary)",
                    border: active ? "0" : `1px solid ${c.bg}`,
                    boxShadow: active ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                    transition: "all 150ms ease",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 8, height: 8, borderRadius: 999,
                      background: active ? c.fg : c.bg,
                      opacity: active ? 0.9 : 1,
                    }}
                  />
                  {tier}
                  <span style={{ ...mono, fontSize: 11, opacity: active ? 0.95 : 0.7 }}>{tierCounts[tier]}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Search + region filter + actions */}
        <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <input
              data-testid="xray-cohort-search"
              placeholder="Search name, role, region, tier…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="text-sm"
              style={{
                padding: "10px 14px",
                border: "var(--border-width) solid var(--border-default)",
                borderRadius: 8,
                width: 320,
                background: "var(--surface-inner)",
                color: "var(--text-primary)",
                outline: "none",
              }}
            />
            <select
              data-testid="region-filter-select"
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              className="text-sm"
              style={{
                padding: "10px 14px",
                border: "var(--border-width) solid var(--border-default)",
                borderRadius: 8,
                background: "var(--surface-inner)",
                color: "var(--text-primary)",
                outline: "none",
                cursor: "pointer",
                minWidth: 180,
              }}
            >
              <option value="ALL">All countries ({regionOptions.length})</option>
              {regionOptions.map((rgn) => (
                <option key={rgn} value={rgn}>{rgn}</option>
              ))}
            </select>
            {(tierFilter || regionFilter !== "ALL" || search) && (
              <button
                data-testid="clear-filters-btn"
                onClick={() => { setTierFilter(null); setRegionFilter("ALL"); setSearch(""); }}
                className="text-xs font-medium"
                style={{
                  padding: "9px 14px",
                  border: `1px solid var(--border-default)`,
                  background: "transparent",
                  color: "var(--text-secondary)",
                  borderRadius: 8,
                  cursor: "pointer",
                }}
              >
                Clear filters
              </button>
            )}
            <div data-testid="filtered-count" className="text-xs" style={ts}>
              Showing <span style={{ ...mono, ...tp, fontWeight: 700 }}>{filtered.length}</span> of <span style={{ ...mono, fontWeight: 700 }}>{state.items.length}</span>
            </div>
          </div>
          <button
            data-testid="back-to-admin-btn"
            onClick={() => navigate(backPath)}
            className="text-sm font-medium"
            style={{ padding: "9px 16px", border: `1px solid ${SAGICOR_BLUE}`, background: "transparent", color: SAGICOR_BLUE_DARK, borderRadius: 8, cursor: "pointer" }}
          >
            ← Back to {isExecRoute ? "Dashboard" : "Admin"}
          </button>
        </div>

        {/* Methodology orientation panel — sits at the top of the cohort table */}
        <MethodologyPanel />

        {/* Cohort table */}
        {filtered.length === 0 ? (
          <div data-testid="xray-cohort-empty" style={{ ...crd, padding: 40, textAlign: "center", color: "var(--text-secondary)" }}>
            No X-Rays match your search.
          </div>
        ) : (
          <div style={crd} className="overflow-hidden">
            <table data-testid="xray-cohort-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "var(--surface-inner)", textAlign: "left" }}>
                  {["#", "Candidate", "Role", "Region", "Tier", "BRI", "Confidence", "Verdict", "Disposition", "Generated", ""].map((h, i) => (
                    <th key={i} style={{ padding: "12px 14px", borderBottom: "var(--border-width) solid var(--border-default)", ...labelS, color: "var(--text-secondary)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr
                    key={r.candidate_id}
                    data-testid={`xray-cohort-row-${r.candidate_id}`}
                    style={{ borderBottom: "var(--border-width) solid var(--border-default)" }}
                  >
                    <td style={{ padding: "14px", ...tm, ...mono }}>{i + 1}</td>
                    <td style={{ padding: "14px" }}>
                      <div className="font-semibold" style={tp}>
                        {r.name || "(unnamed)"}
                      </div>
                      <div style={{ ...tm, ...mono, fontSize: 11, marginTop: 2 }}>{r.candidate_id}</div>
                    </td>
                    <td style={{ padding: "14px" }}>
                      <div style={tp}>{r.role || "—"}</div>
                      <div style={{ ...tm, fontSize: 11, marginTop: 2 }}>{r.unit || "—"}</div>
                    </td>
                    <td style={{ padding: "14px" }}>
                      <span
                        data-testid={`region-pill-${r.candidate_id}`}
                        style={{
                          display: "inline-block",
                          padding: "3px 10px",
                          background: SAGICOR_BLUE_PALE,
                          color: SAGICOR_BLUE_DARK,
                          border: `1px solid ${SAGICOR_BLUE}`,
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {r.region || "—"}
                      </span>
                    </td>
                    <td style={{ padding: "14px" }}><TierBadge tier={r.tier} /></td>
                    <td style={{ padding: "14px", ...mono, ...tp, fontWeight: 600 }}>{r.bri_score || "—"}</td>
                    <td style={{ padding: "14px", ...ts, fontSize: 12 }}>{r.confidence || "—"}</td>
                    <td style={{ padding: "14px", ...ts, fontSize: 12 }}>{r.verdict || "—"}</td>
                    <td style={{ padding: "14px", ...ts, fontSize: 12 }}>{r.disposition || "—"}</td>
                    <td style={{ padding: "14px", ...tm, ...mono, fontSize: 11, whiteSpace: "nowrap" }}>{fmtDate(r.generated_at)}</td>
                    <td style={{ padding: "14px", textAlign: "right" }}>
                      <button
                        data-testid={`xray-view-btn-${r.candidate_id}`}
                        onClick={() => navigate(`${isExecRoute ? "/executive" : "/admin"}/xray-template-preview/${r.candidate_id}`)}
                        className="text-xs font-semibold tracking-wide"
                        style={{
                          padding: "8px 14px",
                          background: `linear-gradient(135deg, ${SAGICOR_BLUE} 0%, ${SAGICOR_GREEN} 100%)`,
                          color: "#FFF",
                          border: 0, borderRadius: 8, cursor: "pointer",
                          boxShadow: "0 1px 3px rgba(0,153,204,0.30)",
                        }}
                      >
                        View X-Ray →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
